/* eslint-disable max-len */
import { ChantContext } from "../../gregorio-chant/ChantContext";
import { ChantLayoutElement } from "../layout/ChantLayoutElement";
import { Line, Point, Rect } from "../../drawing";
import { GlyphVisualizer } from "./GlyphVisualizer";
import { calculateHeightPositionFromPitch } from "../utils";
import { GrehepisemaSize } from "../episemas/enums";
import { GregorioNoteElement, HEPISEMA } from "../../gregorio-chant/structures/GregorioNoteElement";
import { GregorioSegment } from "../../gregorio-chant/structures/GregorioSegment";
import { GregorioType } from "../../gregorio-chant/enums/GregorioType";
import { orderBy, sortBy } from "lodash";
import { GregorioShape } from "../../gregorio-chant/enums/GregorioShape";

function isVirga(note: GregorioNoteElement) {
  return (
    note.shape === GregorioShape.VIRGA ||
    note.shape === GregorioShape.VIRGA_REVERSA ||
    note.shape === GregorioShape.BIVIRGA ||
    note.shape === GregorioShape.TRIVIRGA
  );
}

interface NoteHepisema {
  y: number;
  i: number;
  connect: boolean;
  size: GrehepisemaSize;
  isLowerNote: boolean;
}

export class HorizontalEpisema extends ChantLayoutElement {
  _segment: GregorioSegment;
  _lines: Line[] = [];

  _noteWidth = this._context.pitchWidth;
  _smallHepisemaWidth = this._context.pitchWidth / 1.5;

  constructor(context: ChantContext, segment: GregorioSegment) {
    super(context);
    this._segment = segment;
  }

  performLayout(noteElements: ChantLayoutElement[]) {
    const glyphVisualizers = noteElements.filter(
      (e) => e instanceof GlyphVisualizer
    ) as GlyphVisualizer[];
    let gvIndex = 0;
    const aboveLines: Line[] = [];
    const belowLines: Line[] = [];

    for (let eIndex = 0; eIndex < this._segment.realElements.length; eIndex++) {
      const element = this._segment.realElements[eIndex];
      if (element.type !== GregorioType.ELEMENT) {
        continue;
      }

      for (let gIndex = 0; gIndex < element.glyphs.length; gIndex++) {
        const glyph = element.glyphs[gIndex];
        if (glyph.type !== GregorioType.GLYPH) {
          continue;
        }

        const elementGlyphVisualizers = glyphVisualizers.slice(
          gvIndex,
          gvIndex + glyph.visualizersCount
        );
        gvIndex += glyph.visualizersCount;

        if (!glyph.hasEpisema) {
          continue;
        }

        if (elementGlyphVisualizers.length === 0) {
          continue;
        }

        const notes = glyph.notes.allNotes;
        const startX = elementGlyphVisualizers[0]?.bounds.x ?? 0;

        const above: NoteHepisema[] = [];
        const below: NoteHepisema[] = [];

        const { maxAbove, minBelow, maxPitch, minPitch } = this.getGlyphMetrics(notes);

        for (let nIndex = 0; nIndex < notes.length; nIndex++) {
          const note = notes[nIndex];
          const nextNote =
            notes[nIndex + 1] ||
            element.glyphs[gIndex + 1]?.firstNote ||
            this._segment.realElements[eIndex + 1]?.first_glyph.firstNote ||
            null;
          const nextGlyph =
            element.glyphs[gIndex + 1] ||
            this._segment.realElements[eIndex + 1]?.first_glyph ||
            null;
          const isNextNoteFromNextElement = nIndex === glyph.notes.notes.length - 1;

          const prevNote =
            notes[nIndex - 1] ||
            element.glyphs[gIndex - 1]?.lastNote ||
            this._segment.realElements[eIndex - 1]?.last_glyph?.lastNote ||
            null;
          const prevglyph =
            element.glyphs[gIndex - 1] ||
            this._segment.realElements[eIndex - 1]?.last_glyph ||
            null;
          const isPrevNoteFromPrevElement = nIndex === 0;

          if (
            note.h_episema_above &&
            (note.h_episema_above_size || note.h_episema_above_size === GrehepisemaSize.H_NORMAL)
          ) {
            // const hepisema_pitch = maxAbove === HEPISEMA.FORCED ||
            //   maxAbove === HEPISEMA.AUTO ? maxPitch + 1 : maxAbove;

            let hepisema_pitch = Math.max(maxPitch + 1, maxAbove);

            if (prevNote) {
              if (
                isNextNoteFromNextElement &&
                prevNote.h_episema_above_connect &&
                nextNote &&
                nextNote.h_episema_above &&
                nextNote.h_episema_above !== HEPISEMA.FORCED &&
                (nextNote.h_episema_above_size ||
                  nextNote.h_episema_above_size === GrehepisemaSize.H_NORMAL)
              ) {
                const nextGlyphMetrics = this.getGlyphMetrics(nextGlyph.notes.allNotes);
                hepisema_pitch = Math.max(
                  hepisema_pitch,
                  nextGlyphMetrics.maxAbove,
                  nextGlyphMetrics.maxPitch + 1
                );
              }

              if (
                isPrevNoteFromPrevElement &&
                prevNote.h_episema_above_connect &&
                prevNote &&
                prevNote.h_episema_above &&
                prevNote.h_episema_above !== HEPISEMA.FORCED &&
                (prevNote.h_episema_above_size ||
                  prevNote.h_episema_above_size === GrehepisemaSize.H_NORMAL)
              ) {
                const prevGlyphMetrics = this.getGlyphMetrics(prevglyph.notes.allNotes);
                hepisema_pitch = Math.max(
                  hepisema_pitch,
                  prevGlyphMetrics.maxAbove,
                  prevGlyphMetrics.maxPitch + 1
                );
                note.h_episema_above = hepisema_pitch;
              }
            }

            let y = calculateHeightPositionFromPitch(this._context, hepisema_pitch);
            if (hepisema_pitch % 2 === 0) {
              y -= 5;
            } else {
              y -= 2;
            }

            above.push({
              y,
              i: nIndex,
              connect:
                note.h_episema_above_connect &&
                nextNote &&
                !!(
                  nextNote.h_episema_above &&
                  nextNote.h_episema_above !== HEPISEMA.FORCED &&
                  (nextNote.h_episema_above_size ||
                    nextNote.h_episema_above_size === GrehepisemaSize.H_NORMAL)
                ) &&
                !(isVirga(note) && isVirga(nextNote)),
              size: note.h_episema_above_size,
              isLowerNote: note.is_lower_note,
            });
          } else if (
            note.h_episema_below &&
            (note.h_episema_below_size || note.h_episema_below_size === GrehepisemaSize.H_NORMAL)
          ) {
            const hepisema_pitch = Math.max(minPitch - 1, minBelow);
            let y = calculateHeightPositionFromPitch(this._context, hepisema_pitch);

            if (hepisema_pitch % 2 === 0) {
              y += 4;
            } else {
              y += 2;
            }

            below.push({
              y,
              i: nIndex,
              connect:
                nextNote &&
                !!(
                  nextNote.h_episema_below &&
                  nextNote.h_episema_below !== HEPISEMA.FORCED &&
                  (nextNote.h_episema_below_size ||
                    nextNote.h_episema_below_size === GrehepisemaSize.H_NORMAL)
                ),
              size: note.h_episema_below_size,
              isLowerNote: note.is_lower_note,
            });
          }
        }

        const addLiness = (arr: NoteHepisema[]): Line[] => {
          const lines: Line[] = [];
          for (let i = arr.length - 1; i >= 0; i--) {
            const note = arr[i];
            const next = arr[i + 1];

            let noteStartX,
              noteEndX = 0;
            if ((notes.length === 4 && note.i === 0) || (notes.length === 5 && note.i === 1)) {
              noteStartX = startX;
              if (note.connect && next && next.i === note.i + 1) {
                noteEndX = startX * (note.i + 1) + this._noteWidth * 2;
              } else {
                noteEndX = startX * (note.i + 1) + this._noteWidth;
              }
            } else if (notes.length === 5 && note.i === 0) {
              noteEndX = startX;
              noteStartX = startX - this._noteWidth;
            } else {
              const lastVisualizer = elementGlyphVisualizers[elementGlyphVisualizers.length - 1];
              if (note.connect && next && next.i === note.i + 1) {
                noteEndX =
                  lastVisualizer.bounds.right - this._noteWidth * (notes.length - note.i - 2);
                noteStartX = noteEndX - this._noteWidth * 2;
              } else if (note.connect && note.i === notes.length - 1) {
                noteEndX = lastVisualizer.bounds.right + this._noteWidth;
                noteStartX = noteEndX - this._noteWidth * 2;
              } else {
                noteEndX = lastVisualizer.bounds.right;
                if (!note.isLowerNote) {
                  if (elementGlyphVisualizers.length > 1) {
                    const noteVisualizer = elementGlyphVisualizers[note.i];
                    if (!noteVisualizer) {
                      continue;
                    }
                    noteEndX = noteVisualizer.bounds.right;
                    noteStartX = noteEndX - this._noteWidth;
                  } else {
                    noteEndX -= this._noteWidth * (notes.length - note.i - 1);
                  }
                }
                noteStartX = noteEndX - this._noteWidth;
              }

              if (noteStartX < startX) {
                noteEndX = startX + noteEndX - noteStartX;
                noteStartX = startX;
              }
            }

            const fullWidth = noteEndX - noteStartX;
            switch (note.size) {
              case GrehepisemaSize.H_NORMAL:
                lines.push(Line.createHorizontalLine(noteStartX, note.y, fullWidth));
                break;
              case GrehepisemaSize.H_SMALL_LEFT:
                lines.push(Line.createHorizontalLine(noteStartX, note.y, this._smallHepisemaWidth));
                break;
              case GrehepisemaSize.H_SMALL_CENTRE:
                lines.push(
                  Line.createHorizontalLine(
                    noteStartX + this._smallHepisemaWidth / 2,
                    note.y,
                    this._smallHepisemaWidth
                  )
                );
                break;
              case GrehepisemaSize.H_SMALL_RIGHT:
                lines.push(
                  Line.createHorizontalLine(
                    noteEndX - this._smallHepisemaWidth,
                    note.y,
                    this._smallHepisemaWidth
                  )
                );
                break;
            }
          }

          return lines;
        };

        if (above.length > 0) {
          aboveLines.push(...addLiness(above));
        }
        if (below.length > 0) {
          belowLines.push(...addLiness(below));
        }
      }
    }

    this._lines = orderBy(
      [...this.mergeLines(aboveLines), ...this.mergeLines(belowLines)],
      (l) => l.start.x
    );

    if (this._lines.length > 0) {
      this.bounds = this._lines
        .slice(1)
        .reduce((bounds, line) => bounds.union(line.toRect()), this._lines[0].toRect());
    }
  }

  private mergeLines(linesParam: Line[]): Line[] {
    if (linesParam.length === 0) {
      return [];
    }

    const lines = sortBy(linesParam, (l) => l.start.x);

    const mergedLines: Line[] = [];
    mergedLines.push(lines[0]);

    for (let i = 1; i < lines.length; i++) {
      const lastMergedLine = mergedLines[mergedLines.length - 1];
      const currentLine = lines[i];

      if (currentLine.start.x <= lastMergedLine.end.x) {
        // Merge overlapping lines
        mergedLines[mergedLines.length - 1] = Line.create(
          Point.create(
            lastMergedLine.start.x,
            Math.min(lastMergedLine.start.y, currentLine.start.y)
          ),
          Point.create(
            Math.max(lastMergedLine.end.x, currentLine.end.x),
            Math.min(lastMergedLine.start.y, currentLine.start.y)
          )
        );
      } else {
        // Add the line as it doesn't overlap
        mergedLines.push(currentLine);
      }
    }

    return mergedLines;
  }

  getGlyphMetrics(notes: GregorioNoteElement[]) {
    const maxAbove = Math.max(...notes.map((n) => n.h_episema_above ?? 0).filter((n) => n !== 0));
    const minBelow = Math.max(...notes.map((n) => n.h_episema_below ?? 0).filter((n) => n !== 0));
    const maxPitch = Math.max(...notes.map((n) => n.pitch));
    const minPitch = Math.min(...notes.map((n) => n.pitch));

    return { maxAbove, minBelow, maxPitch, minPitch };
  }

  public createSvgFragments(): SVGElement[] {
    const elements: SVGElement[] = [];
    for (const line of this._lines) {
      const el = document.createElementNS("http://www.w3.org/2000/svg", "line");
      el.setAttribute("x1", String(line.start.x));
      el.setAttribute("y1", String(line.start.y));
      el.setAttribute("x2", String(line.end.x));
      el.setAttribute("y2", String(line.end.y));

      el.setAttribute("stroke", "#000000");
      el.setAttribute("stroke-width", "1.5");

      el.classList.add("HorizontalEpisema");

      elements.push(el);
    }

    return [...super.createSvgFragments(), ...elements];
  }
}
